import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { getTenantToday, getNextBusinessDay, parseDateString } from '../utils/dateUtils';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scale,
  RefreshCcw,
  Activity,
  Zap,
  ChevronRight,
  Target,
  History,
  Settings,
  MoreVertical,
  PlusCircle,
  AlertCircle,
  TrendingUp,
  PieChart,
  Layers,
  Cpu,
  Bot,
  Sparkles,
  ToggleLeft,
  ToggleRight,
  DollarSign,
  Lock,
  Clock,
  FileText,
  X,
  Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';

import { DateField } from './shared/DateField';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Styled Components ==============
const Container = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  background: transparent;
`;

const Sidebar = styled.div`
  width: 360px;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2.5rem;
  background: transparent;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

// ============== Sleeve Allocation Components ==============
const SleeveCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin: 20px 0;
`;

const SleeveCard = styled.div<{ $drift?: boolean }>`
  background: ${props => props.$drift ? 'rgba(245, 158, 11, 0.08)' : 'rgba(30, 41, 59, 0.8)'};
  border: 1px solid ${props => props.$drift ? 'rgba(245, 158, 11, 0.3)' : '#334155'};
  border-radius: 10px;
  padding: 14px;
  text-align: center;
`;

const SleeveLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const SleeveValue = styled.div<{ $color: string }>`
  font-size: 1.25rem;
  font-weight: 800;
  color: ${props => props.$color};
`;

const SleeveDrift = styled.div<{ $negative?: boolean }>`
  font-size: 0.65rem;
  font-weight: 600;
  margin-top: 4px;
  color: ${props => props.$negative ? '#f87171' : '#34d399'};
`;

// ============== Liquidity Badge ==============
const LiquidityBadge = styled.span<{ $type: 'tradable' | 'notice' | 'locked' | 'non-tradable' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-left: 8px;
  ${props => {
    switch (props.$type) {
      case 'tradable': return 'background: rgba(16,185,129,0.15); color: #6ee7b7; border: 1px solid rgba(16,185,129,0.25);';
      case 'notice': return 'background: rgba(245,158,11,0.15); color: #fcd34d; border: 1px solid rgba(245,158,11,0.25);';
      case 'locked': return 'background: rgba(239,68,68,0.15); color: #fca5a5; border: 1px solid rgba(239,68,68,0.25);';
      case 'non-tradable': return 'background: rgba(100,116,139,0.15); color: #94a3b8; border: 1px solid rgba(100,116,139,0.25);';
    }
  }}
`;

// ============== Toggle Switch ==============
const ToggleContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px 12px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  user-select: none;
  &:hover {
    background: rgba(15, 23, 42, 0.8);
  }
`;

const ToggleTrack = styled.div<{ $active: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${props => props.$active ? '#3b82f6' : '#334155'};
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
`;

const ToggleThumb = styled.div<{ $active: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 2px;
  left: ${props => props.$active ? '18px' : '2px'};
  transition: left 0.2s ease;
`;

// ============== Unfunded Commitments Card ==============
const CommitmentCard = styled(GlassCard)`
  padding: 24px;
`;

const CommitmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const CommitmentItem = styled.div`
  text-align: center;
  padding: 12px;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const CommitmentLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 6px;
`;

const CommitmentValue = styled.div`
  font-size: 1.1rem;
  font-weight: 800;
  color: #e2e8f0;
`;

// ============== Three-tier Action Button ==============
const ActionButton = styled.button<{ $tier: 'execute' | 'notice' | 'manual' }>`
  padding: 6px 14px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  ${props => {
    switch (props.$tier) {
      case 'execute': return 'background: rgba(16,185,129,0.15); color: #6ee7b7; border: 1px solid rgba(16,185,129,0.25); &:hover { background: rgba(16,185,129,0.25); }';
      case 'notice': return 'background: rgba(245,158,11,0.15); color: #fcd34d; border: 1px solid rgba(245,158,11,0.25); &:hover { background: rgba(245,158,11,0.25); }';
      case 'manual': return 'background: transparent; color: #94a3b8; border: 1px solid rgba(100,116,139,0.25); cursor: default;';
    }
  }}
`;

// ============== Constants ==============
const SLEEVE_BUCKETS = [
  { key: 'publicFI', label: 'Public FI', color: '#3b82f6' },
  { key: 'publicEquity', label: 'Public Equity', color: '#10b981' },
  { key: 'privateCredit', label: 'Private Credit', color: '#8b5cf6' },
  { key: 'reEquity', label: 'RE Equity', color: '#f59e0b' },
  { key: 'reDebt', label: 'RE Debt', color: '#ef4444' },
  { key: 'peVc', label: 'PE / VC', color: '#06b6d4' },
  { key: 'hedgeFunds', label: 'Hedge Funds', color: '#ec4899' },
  { key: 'cashReserve', label: 'Cash Reserve', color: '#64748b' },
];

// ============== Helper Functions ==============
const mapPositionToSleeve = (position: any): string => {
  const ac = (position.assetClassName || position.assetClass || '').toLowerCase();
  const st = (position.securityType || '').toLowerCase();
  const combined = ac + ' ' + st;

  // Cash
  if (combined.includes('cash') || combined.includes('money market')) return 'cashReserve';

  // RE Debt (before generic RE)
  if (ac.includes('re_debt') || ac.includes('re debt') || st.includes('re_debt')
      || (combined.includes('real estate') && combined.includes('debt'))
      || combined.includes('cmbs'))
    return 'reDebt';

  // RE Equity / REIT
  if (ac.includes('real_estate_equity') || ac.includes('re equity') || ac.includes('real estate equity')
      || st.includes('real_estate_equity') || st.includes('reit')
      || ac.includes('real_estate_fund') || st.includes('real_estate_fund'))
    return 'reEquity';
  if (combined.includes('real estate') || combined.includes('real_estate'))
    return 'reEquity';

  // PE / VC
  if (ac.includes('pe_commitment') || ac.includes('private equity')
      || st.includes('pe_commitment') || combined.includes('venture'))
    return 'peVc';

  // CLO → private credit bucket
  if (combined.includes('clo') || combined.includes('structured credit'))
    return 'privateCredit';

  // Private Credit / Leveraged Loans
  if (ac.includes('private_credit') || ac.includes('private credit')
      || st.includes('private_credit') || combined.includes('term_loan')
      || combined.includes('senior secured loan') || combined.includes('second lien')
      || combined.includes('leveraged') || combined.includes('loan'))
    return 'privateCredit';

  // Hedge Funds
  if (combined.includes('hedge')) return 'hedgeFunds';

  // Public Equity
  if ((ac.includes('equity') || st.includes('equity') || st.includes('stock'))
      && !combined.includes('clo'))
    return 'publicEquity';

  // Public FI
  if (combined.includes('fixed income') || combined.includes('bond')
      || combined.includes('treasury') || combined.includes('senior secured')
      || combined.includes('senior unsecured') || combined.includes('senior subordinated'))
    return 'publicFI';

  return 'publicFI'; // default
};

const getLiquidityType = (trade: any): 'tradable' | 'notice' | 'locked' | 'non-tradable' => {
  // Use backend classification if available (backend emits tradabilityClassification)
  const backendClassification = trade.tradabilityClassification || trade.liquidityClassification;
  if (backendClassification) {
    const classification = String(backendClassification).toUpperCase();
    if (classification === 'EXECUTABLE_NOW' || classification === 'TRADABLE') return 'tradable';
    if (classification === 'NOTICE_REQUIRED' || classification === 'NOTICE') return 'notice';
    if (classification === 'LOCKED') return 'locked';
    if (classification === 'NON_TRADABLE') return 'non-tradable';
  }

  // Infer from asset class / security type
  const assetClass = trade.assetClassName || trade.assetClass || '';
  const secType = trade.securityType || '';

  if (assetClass === 'Fixed Income' || assetClass === 'Equity' || assetClass === 'Cash') return 'tradable';
  if (assetClass === 'Private Fixed Income') {
    if (secType.includes('TERM_LOAN')) return 'tradable';
    if (secType.includes('PRIVATE_CREDIT')) return 'notice';
    if (secType.includes('PRIVATE_EQUITY')) return 'locked';
  }
  if (assetClass === 'Real Estate') {
    if (secType === 'REIT') return 'tradable';
    if (secType === 'REAL_ESTATE_FUND') return 'notice';
  }
  return 'tradable'; // default
};

const getTradeClassification = (trade: any): 'execute' | 'notice' | 'manual' => {
  // Manual-review trades (e.g. leveraged loans) can never one-click execute.
  if (trade.requiresManualReview === true) return 'manual';
  // Use backend classification if available (backend emits tradabilityClassification)
  const backendTier = trade.tradabilityClassification || trade.executionTier;
  if (backendTier) {
    const tier = String(backendTier).toUpperCase();
    if (tier === 'EXECUTABLE_NOW' || tier === 'EXECUTE') return 'execute';
    if (tier === 'NOTICE_REQUIRED' || tier === 'NOTICE') return 'notice';
    if (tier === 'NON_TRADABLE' || tier === 'MANUAL') return 'manual';
  }

  // Fallback: infer from liquidity type
  const liq = getLiquidityType(trade);
  if (liq === 'tradable') return 'execute';
  if (liq === 'notice') return 'notice';
  return 'manual';
};

const getLiquidityLabel = (type: 'tradable' | 'notice' | 'locked' | 'non-tradable'): string => {
  switch (type) {
    case 'tradable': return 'Tradable';
    case 'notice': return 'Notice';
    case 'locked': return 'Locked';
    case 'non-tradable': return 'Non-Tradable';
  }
};

// Renders an asset-class key (CLO_EQUITY, RE_DEBT, PRIVATE_CREDIT) as a human label ("CLO Equity").
const formatAssetClassLabel = (key: string): string => {
  if (!key) return '';
  // Special-cases for tokens that should stay uppercase
  const upper = new Set(['CLO', 'RE', 'PE', 'VC', 'IG', 'HY', 'IR', 'TR', 'CDS', 'REIT', 'CMBS', 'RMBS', 'ABS', 'TIPS']);
  return key
    .split(/[_\s]+/)
    .map(tok => upper.has(tok.toUpperCase()) ? tok.toUpperCase() : tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase())
    .join(' ');
};

// Deterministic color per asset-class family. Keeps the strip institutional-clean.
const assetClassColor = (key: string): string => {
  const k = (key || '').toUpperCase();
  if (k.startsWith('CLO_EQUITY')) return '#a78bfa';      // violet
  if (k.startsWith('CLO_SENIOR')) return '#60a5fa';      // blue
  if (k.startsWith('CLO_MEZZ')) return '#818cf8';        // indigo
  if (k.startsWith('CLO')) return '#a78bfa';
  if (k.includes('CASH')) return '#94a3b8';              // slate
  if (k.startsWith('RE_EQUITY') || k === 'REIT') return '#f59e0b';   // amber
  if (k.startsWith('RE_DEBT') || k.includes('CMBS')) return '#ef4444'; // red
  if (k.startsWith('PRIVATE_EQUITY') || k === 'PE') return '#06b6d4';  // cyan
  if (k.startsWith('PRIVATE_CREDIT')) return '#8b5cf6';   // violet
  if (k.includes('LEVERAGED')) return '#6366f1';          // indigo
  if (k.includes('HY_BOND') || k.includes('HIGH_YIELD')) return '#ec4899'; // pink
  if (k.includes('INFRASTRUCTURE')) return '#10b981';     // emerald
  if (k.includes('DISTRESSED')) return '#dc2626';         // red-600
  if (k.includes('EQUITY')) return '#10b981';             // emerald (public equity)
  if (k.includes('FIXED_INCOME') || k.includes('BOND')) return '#3b82f6'; // blue
  return '#64748b'; // slate default
};

const getActionLabel = (tier: 'execute' | 'notice' | 'manual'): string => {
  switch (tier) {
    case 'execute': return 'Execute Now';
    case 'notice': return 'Submit Notice';
    case 'manual': return 'Manual Action';
  }
};

const PortfolioRebalancingScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<any[]>([]);
  
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(getTenantToday());
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [autoExecute, setAutoExecute] = useState(false);
  
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiExplanationLoading, setAiExplanationLoading] = useState(false);
  

  const [showTargetAllocationModal, setShowTargetAllocationModal] = useState(false);
  const [targetAllocations, setTargetAllocations] = useState<any>(null);
  const [targetAllocationHistory, setTargetAllocationHistory] = useState<any[]>([]);
  // Manage Targets modal form state — bound to /api/target-allocations CRUD.
  const [targetForm, setTargetForm] = useState<{
    strategyName: string;
    rows: Array<{ assetClass: string; pct: string }>;
    existingId: number | null;
  }>({ strategyName: '', rows: [], existingId: null });
  const [targetSaving, setTargetSaving] = useState(false);
  const [targetSaveError, setTargetSaveError] = useState<string | null>(null);
  const [assetClassOptions, setAssetClassOptions] = useState<string[]>([]);

  // Execute Now → stage DRAFT trade + submit for pre-clear, all from the rebalancer card.
  // confirmStageIdx: row index awaiting user confirmation (null = no confirm open)
  // stageStatus: per-row state once user confirms (submitting → staged | failed)
  // 'failed' = transient (retryable); 'blocked' = deterministic compliance
  // hold — the draft exists and the next step is the override workflow.
  type StageStatus = { state: 'submitting' | 'staged' | 'failed' | 'blocked'; tradeId?: number; error?: string };
  const [confirmStageIdx, setConfirmStageIdx] = useState<number | null>(null);
  const [stageStatus, setStageStatus] = useState<Record<number, StageStatus>>({});
  // Map a rebalancer allocation key (e.g. "Cash", "Senior Secured Loan", "RE_DEBT")
  // to its sleeve bucket key.
  const mapAllocKeyToSleeve = (key: string): string => {
    const k = key.toLowerCase().replace(/[\s_]+/g, '');
    if (k.includes('cash') || k.includes('moneymarket')) return 'cashReserve';
    if (k.includes('redebt') || k.includes('realestatedebt') || k.includes('cmbs')) return 'reDebt';
    if (k.includes('realestateequity') || k.includes('reequity') || k.includes('reit') || k.includes('realestatefund')) return 'reEquity';
    if (k.includes('realestate')) return 'reEquity';
    if (k.includes('pecommitment') || k.includes('privateequity') || k.includes('venture') || k.includes('pevc') || k.includes('pe/vc')) return 'peVc';
    if (k.includes('clo') || k.includes('structuredcredit')) return 'privateCredit';
    if (k.includes('privatecredit') || k.includes('privateloan') || k.includes('termloan')
        || k.includes('seniorsecuredloan') || k.includes('secondlien') || k.includes('leveraged') || k.includes('loan')) return 'privateCredit';
    if (k.includes('hedge')) return 'hedgeFunds';
    if (k.includes('equity') && !k.includes('clo')) return 'publicEquity';
    if (k.includes('fixedincome') || k.includes('bond') || k.includes('treasury')
        || k.includes('seniorsecured') || k.includes('seniorunsecured') || k.includes('seniorsubordinated')) return 'publicFI';
    return 'publicFI';
  };

  // Compute sleeve allocations from rebalancer current_allocations + target_allocations,
  // or fall back to position-level mapping if the response includes a positions array.
  const sleeveAllocations = useMemo(() => {
    const allocations: Record<string, { value: number; pct: number; targetPct: number }> = {};
    SLEEVE_BUCKETS.forEach(b => {
      allocations[b.key] = { value: 0, pct: 0, targetPct: 0 };
    });

    // Primary path: use the rebalancer's pre-computed current_allocations
    const currentAlloc = results?.current_allocation || results?.current_allocations || results?.currentAllocations;
    if (currentAlloc && Object.keys(currentAlloc).length > 0) {
      Object.entries(currentAlloc).forEach(([key, val]: [string, any]) => {
        const pct = typeof val === 'number' ? val : (val?.percentage ?? val?.pct ?? 0);
        const sleeveKey = mapAllocKeyToSleeve(key);
        allocations[sleeveKey].pct += pct;
      });
    } else {
      // Fallback: derive from positions array if present
      const positions = results?.portfolios || results?.positions || [];
      if (positions.length > 0) {
        const totalValue = positions.reduce((sum: number, p: any) => sum + (p.afterVal || p.marketValue || p.value || 0), 0);
        if (totalValue > 0) {
          positions.forEach((pos: any) => {
            const sleeve = mapPositionToSleeve(pos);
            allocations[sleeve].value += (pos.afterVal || pos.marketValue || pos.value || 0);
          });
          SLEEVE_BUCKETS.forEach(b => {
            allocations[b.key].pct = (allocations[b.key].value / totalValue) * 100;
          });
        }
      }
    }

    // Map target allocations
    const targetAlloc = results?.target_allocations || results?.target_allocation || results?.targetAllocations;
    if (targetAlloc) {
      Object.entries(targetAlloc).forEach(([key, val]: [string, any]) => {
        const target = typeof val === 'number' ? val : (val?.target ?? val?.percentage ?? 0);
        const sleeveKey = mapAllocKeyToSleeve(key);
        allocations[sleeveKey].targetPct += target;
      });
    }

    return allocations;
  }, [results]);

  // Commitment tiles from the REAL service shapes (F-RB-013): commitmentSummary
  // is {totalCommitted, totalCalled, totalUnfunded, calledPercentage, ...} and
  // capitalCallProjections is a LIST of per-month {projectedDate, projectedAmount}.
  const commitmentSummary = useMemo(() => {
    const summary = results?.commitmentSummary;
    const projections: any[] = Array.isArray(results?.capitalCallProjections)
      ? results.capitalCallProjections : [];
    if (!summary && projections.length === 0) return null;
    const sumWithinMonths = (months: number) => {
      const cutoff = Date.now() + months * 30.44 * 86400000;
      return projections.reduce((acc, p) => {
        const t = p?.projectedDate ? new Date(p.projectedDate).getTime() : NaN;
        return !isNaN(t) && t <= cutoff ? acc + Number(p.projectedAmount || 0) : acc;
      }, 0);
    };
    return {
      totalUnfunded: summary?.totalUnfunded != null ? Number(summary.totalUnfunded) : null,
      projected6mo: projections.length ? sumWithinMonths(6) : null,
      projected12mo: projections.length ? sumWithinMonths(12) : null,
      calledPercentage: summary?.calledPercentage != null ? Number(summary.calledPercentage) : null,
    };
  }, [results]);

  useEffect(() => {
    fetchPortfolios();
  }, []);

  useEffect(() => {
    if (selectedPortfolioId) {
      fetchTargetAllocations();
    }
  }, [selectedPortfolioId, selectedDate]);

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get('/api/dimensions/portfolios', {
        params: { tradingEnabled: true },
      });
      const data = response.data?.content || response.data || [];
      setPortfolios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    }
  };

  const handleRebalance = async () => {
    if (!selectedPortfolioId) return;
    setLoading(true);
    setResults(null);
    setAiExplanation(null);
    try {
      const url = `/api/agents/rebalancing/portfolio/${selectedPortfolioId}?autoExecute=${autoExecute}${selectedDate ? `&asOfDate=${selectedDate}` : ''}`;
      const response = await axios.post(url);
      setResults(response.data);
    } catch (error: any) {
      console.error('Error rebalancing portfolio:', error);
      setResults({
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTargetAllocations = async () => {
    if (!selectedPortfolioId) return;
    try {
      const response = await axios.get(`/api/target-allocations/portfolio/${selectedPortfolioId}?asOfDate=${selectedDate}`);
      setTargetAllocations(response.data);
      const historyResponse = await axios.get(`/api/target-allocations/portfolio/${selectedPortfolioId}/history`);
      setTargetAllocationHistory(historyResponse.data || []);
    } catch (error) {
      console.error('Error fetching target allocations:', error);
      setTargetAllocations(null);
    }
  };

  // Fetch the canonical asset-class list once so the Manage Targets dropdown matches dim_asset_class.
  useEffect(() => {
    axios.get('/api/dimensions/asset-classes?size=200')
      .then(r => {
        const list = (r.data?.content || r.data || [])
          .map((a: any) => a.assetClassName)
          .filter(Boolean);
        setAssetClassOptions(list);
      })
      .catch(() => setAssetClassOptions([]));
  }, []);

  // When Manage Targets opens, seed the form from the existing active target (if any)
  // or from the asset classes currently held by the portfolio.
  useEffect(() => {
    if (!showTargetAllocationModal) return;
    const allocations = targetAllocations?.allocations || {};
    const activeRow = (targetAllocationHistory || []).find((h: any) => h?.status === 'ACTIVE');
    let rows: Array<{ assetClass: string; pct: string }> = [];
    if (Object.keys(allocations).length > 0) {
      rows = Object.entries(allocations).map(([assetClass, pct]: [string, any]) => ({
        assetClass,
        pct: String(Number(pct) || 0),
      }));
    } else {
      // No existing target — seed with the asset classes already in the portfolio
      const driftKeys = Object.keys(
        results?.drift_details
          || results?.current_allocation
          || results?.current_allocations
          || {}
      );
      if (driftKeys.length > 0) {
        const equalPct = +(100 / driftKeys.length).toFixed(2);
        rows = driftKeys.map(k => ({ assetClass: k, pct: String(equalPct) }));
      }
    }
    setTargetForm({
      strategyName: targetAllocations?.strategyName || activeRow?.strategyName || '',
      rows,
      existingId: activeRow?.id || null,
    });
    setTargetSaveError(null);
  }, [showTargetAllocationModal, targetAllocations, targetAllocationHistory, results]);

  const targetFormTotal = useMemo(
    () => targetForm.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0),
    [targetForm.rows]
  );

  const handleTargetRowChange = (idx: number, field: 'assetClass' | 'pct', value: string) => {
    setTargetForm(prev => ({
      ...prev,
      rows: prev.rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  };

  const handleAddTargetRow = () => {
    setTargetForm(prev => ({ ...prev, rows: [...prev.rows, { assetClass: '', pct: '0' }] }));
  };

  const handleRemoveTargetRow = (idx: number) => {
    setTargetForm(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== idx) }));
  };

  const handleSaveTargets = async () => {
    setTargetSaveError(null);
    if (targetForm.rows.length === 0) {
      setTargetSaveError('Add at least one sleeve before saving.');
      return;
    }
    if (Math.abs(targetFormTotal - 100) > 1.0) {
      setTargetSaveError(`Allocations must sum to 100% (currently ${targetFormTotal.toFixed(2)}%).`);
      return;
    }
    // Build the allocations map; drop blanks; reject duplicate asset classes.
    const allocations: Record<string, number> = {};
    for (const r of targetForm.rows) {
      const k = (r.assetClass || '').trim();
      if (!k) {
        setTargetSaveError('Every row needs an asset class.');
        return;
      }
      if (allocations[k] != null) {
        setTargetSaveError(`Asset class "${k}" appears more than once.`);
        return;
      }
      allocations[k] = Number(r.pct) || 0;
    }
    const selectedPortfolio = portfolios.find(p => p.id.toString() === selectedPortfolioId);
    // Use the screen's analysis date (already the tenant-local "today" from getTenantToday()) —
    // NOT new Date().toISOString(), which returns UTC and rolls a day ahead after ~4pm PT,
    // making the row's effective_date > asOfDate so the GET query never returns it.
    const effectiveDate = selectedDate || getTenantToday();
    const payload: any = {
      portfolioId: Number(selectedPortfolioId),
      strategyName: (targetForm.strategyName || '').trim() || 'Custom Target Allocation',
      effectiveDate,
      allocations: JSON.stringify(allocations),
      rebalancingType: 'TARGET_ALLOCATION',
      tenantId: selectedPortfolio?.tenantId,
      status: 'ACTIVE',
    };
    setTargetSaving(true);
    try {
      if (targetForm.existingId) {
        payload.id = targetForm.existingId;
        await axios.put(`/api/target-allocations/${targetForm.existingId}`, payload);
      } else {
        await axios.post('/api/target-allocations', payload);
      }
      await fetchTargetAllocations();
      setShowTargetAllocationModal(false);
      // Re-run analysis so the strip + drift refresh against the new targets.
      // F-RB-009: analysis-only — with Auto-execute ticked, the old rerun
      // silently created drafts from a SAVE-TARGETS click.
      if (autoExecute) setAutoExecute(false);
      handleRebalance();
    } catch (e: any) {
      setTargetSaveError(e?.response?.data?.error || e?.message || 'Failed to save target allocations.');
    } finally {
      setTargetSaving(false);
    }
  };

  const handleExplainDrift = async () => {
    if (!results || !results.drift_detected || !results.drift_details) return;
    setAiExplanationLoading(true);
    setAiExplanation(null);
    try {
      const selectedPortfolio = portfolios.find(p => p.id.toString() === selectedPortfolioId);
      // F-RB-028: the rebalance response carries the real book value — the old
      // derivation summed a nonexistent portfolios array and fed the AI $0.
      const portfolioValue = results.total_market_value
        || results.portfolios?.reduce((sum: number, p: any) => sum + (p.afterVal || 0), 0) || 0;
      
      const response = await axios.post('/api/agents/rebalancing/explain-drift', {
        portfolioName: selectedPortfolio?.portfolioName || 'Portfolio',
        currentAllocations: results.current_allocation || {},
        targetAllocations: results.target_allocation || {},
        driftDetails: results.drift_details || {},
        portfolioValue,
        positionCount: results.number_of_positions || results.portfolios?.length || 0
      }, { timeout: 30000 });

      if (response.data.success && response.data.explanation) {
        setAiExplanation(response.data.explanation);
      } else {
        setAiExplanation(response.data.error || 'Unable to generate explanation');
      }
    } catch (error: any) {
      setAiExplanation(`Unable to generate drift explanation: ${error.message}`);
    } finally {
      setAiExplanationLoading(false);
    }
  };

  // Stage a single recommended trade through the lifecycle:
  //   POST /api/trade-transactions (status=DRAFT)
  //   POST /api/pre-clear/{id}/submit  → PENDING_PRECLEAR
  // The card then flips to a "Staged · Pre-clearing" state with a link to the trade.
  // The plan row carries the backend's evidenced par quantity + price
  // (resolveRealPrice) — they are staged verbatim; a row without a price
  // cannot be staged.
  const handleStageTrade = async (trade: any, idx: number) => {
    if (!selectedPortfolioId) {
      setStageStatus(prev => ({ ...prev, [idx]: { state: 'failed', error: 'No portfolio selected' } }));
      setConfirmStageIdx(null);
      return;
    }
    const priorStatus = stageStatus[idx];
    setStageStatus(prev => ({ ...prev, [idx]: { state: 'submitting' } }));
    setConfirmStageIdx(null);
    try {
      // RETRY: the DRAFT already exists — re-submit it for pre-clear instead
      // of minting a duplicate trade.
      if (priorStatus?.state === 'failed' && priorStatus.tradeId != null) {
        const existingId = priorStatus.tradeId;
        try {
          await axios.post(`/api/pre-clear/${existingId}/submit`);
          setStageStatus(prev => ({ ...prev, [idx]: { state: 'staged', tradeId: existingId } }));
        } catch (preClearErr: any) {
          const msg = preClearErr?.response?.data?.error || preClearErr?.response?.data?.message || preClearErr?.message || 'Pre-clear submission failed';
          setStageStatus(prev => ({ ...prev, [idx]: { state: 'failed', tradeId: existingId, error: msg } }));
        }
        return;
      }
      const tradeDate = selectedDate || getTenantToday();
      const settleDate = getNextBusinessDay(parseDateString(tradeDate));
      const price = trade.price != null ? Number(trade.price) : null;
      if (price == null || !(price > 0)) {
        setStageStatus(prev => ({ ...prev, [idx]: { state: 'failed', error: 'No evidenced price on this trade row — cannot stage' } }));
        return;
      }
      const dto: any = {
        securityId: Number(trade.securityId),
        portfolioId: Number(selectedPortfolioId),
        transactionType: String(trade.transactionType).toUpperCase(),
        // Backend par quantity and price, verbatim — the plan's economics ARE
        // the ticket's economics. No client-side re-derivation.
        amount: Number(trade.amount),
        price,
        quantity: Number(trade.quantity ?? trade.amount),
        tradeDate,
        settleDate,
        status: 'DRAFT',
        notes: trade.reason || 'Created from Portfolio Rebalancer',
      };
      const createResp = await axios.post('/api/trade-transactions', dto);
      const created = createResp.data;
      const tradeId = created?.id;
      if (!tradeId) throw new Error('Trade created but no ID returned');
      try {
        await axios.post(`/api/pre-clear/${tradeId}/submit`);
      } catch (preClearErr: any) {
        // DRAFT was created and survives either way. A 400 is a DETERMINISTIC
        // compliance/validation block — retrying cannot succeed; the governed
        // next step is the PM->CCO override workflow from the draft. Anything
        // else (network, 5xx) is transient and retryable.
        const msg = preClearErr?.response?.data?.error || preClearErr?.response?.data?.message || preClearErr?.message || 'Pre-clear submission failed';
        const isDeterministicBlock = preClearErr?.response?.status === 400;
        setStageStatus(prev => ({ ...prev, [idx]: { state: isDeterministicBlock ? 'blocked' : 'failed', tradeId, error: msg } }));
        return;
      }
      setStageStatus(prev => ({ ...prev, [idx]: { state: 'staged', tradeId } }));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to stage trade';
      setStageStatus(prev => ({ ...prev, [idx]: { state: 'failed', error: msg } }));
    }
  };

  return (
    <Container>
      <Sidebar>
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Rebalancer</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Alignment Engine</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Step 1: Select Portfolio */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <StepNumber>1</StepNumber>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Select Portfolio</h3>
            </div>
            <select 
              value={selectedPortfolioId}
              onChange={(e) => setSelectedPortfolioId(e.target.value)}
              className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none cursor-pointer"
            >
              <option value="">Choose a portfolio...</option>
              {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
            </select>
          </div>

          {/* Step 2: Analysis Date */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <StepNumber>2</StepNumber>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Analysis Date</h3>
            </div>
            <DateField
              value={selectedDate ?? ''}
              className="w-full"
              onChange={iso => setSelectedDate(iso)}
            />
            <label className="flex items-center gap-3 p-3 bg-slate-900/50 border border-white/5 rounded-xl cursor-pointer group hover:bg-slate-900 transition-all">
              <div className={cn(
                "w-5 h-5 rounded border flex items-center justify-center transition-all",
                autoExecute ? "bg-blue-600 border-blue-500" : "border-white/20 group-hover:border-white/40"
              )}>
                {autoExecute && <Zap className="w-3 h-3 text-white fill-current" />}
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={autoExecute}
                onChange={(e) => setAutoExecute(e.target.checked)}
              />
              <span className="text-xs font-semibold text-slate-400 group-hover:text-slate-200 transition-colors">Auto-execute trades</span>
            </label>
          </div>

          {/* Step 3: Actions */}
          <div className="space-y-4 pt-4 border-t border-white/5">
            <div className="flex items-center gap-3">
              <StepNumber>3</StepNumber>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Actions</h3>
            </div>
            <div className="space-y-3">
              <button 
                onClick={handleRebalance}
                disabled={loading || !selectedPortfolioId}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
              >
                {loading ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                Analyze Portfolio
              </button>
              <button 
                onClick={() => setShowTargetAllocationModal(true)}
                disabled={!selectedPortfolioId}
                className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl transition-all border border-white/5 flex items-center justify-center gap-2"
              >
                <Target className="w-4 h-4" />
                Manage Targets
              </button>
            </div>
          </div>
        </div>

      </Sidebar>

      <MainContent>
        <AnimatePresence mode="wait">
          {!results ? (
            <motion.div 
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex flex-col items-center justify-center text-center space-y-6"
            >
              <div className="w-20 h-20 rounded-3xl bg-blue-500/5 border border-blue-500/10 flex items-center justify-center text-blue-500/20">
                <PieChart className="w-10 h-10" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Portfolio Readiness</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
                  Select a portfolio and click "Analyze Portfolio" to check for allocation drift and generate rebalancing trades.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              key="results"
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-8"
            >
              {results.success ? (
                <>
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                        {results.drift_detected ? (
                          <>
                            <AlertCircle className="w-6 h-6 text-amber-400" />
                            Drift Detected
                          </>
                        ) : (
                          <>
                            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                              <Activity className="w-4 h-4 text-emerald-400" />
                            </div>
                            Portfolio Balanced
                          </>
                        )}
                      </h2>
                      <p className="text-slate-500 mt-1">
                        Analysis complete for {portfolios.find(p => p.id.toString() === selectedPortfolioId)?.portfolioName} as of {selectedDate}
                      </p>
                    </div>
                    {results.drift_detected && (
                      <button 
                        onClick={handleExplainDrift}
                        disabled={aiExplanationLoading}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-2 uppercase tracking-widest"
                      >
                        {aiExplanationLoading ? <Bot className="w-4 h-4 animate-bounce" /> : <Sparkles className="w-4 h-4" />}
                        Explain with AI
                      </button>
                    )}
                  </div>

                  <AnimatePresence>
                    {aiExplanation && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-6 overflow-hidden"
                      >
                        <div className="flex items-center gap-3 mb-4 text-indigo-400">
                          <Bot className="w-5 h-5" />
                          <span className="text-xs font-bold uppercase tracking-widest">AI Intelligence Summary</span>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed italic">{aiExplanation}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {results.validation
                    && (results.validation.valid === false
                        || (Array.isArray(results.validation.warnings) && results.validation.warnings.length > 0)) && (
                    <GlassCard className="p-5 border-amber-500/25 bg-amber-500/5">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                        <div>
                          <h3 className="text-sm font-bold text-amber-300">Pre-trade compliance findings</h3>
                          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                            The allocation analysis completed and the recommended trades are shown below.
                            Stage a trade to run the governed pre-clear and override workflow before execution.
                          </p>
                          {Array.isArray(results.validation.errors) && results.validation.errors.length > 0 && (
                            <ul className="mt-3 space-y-1 text-xs text-amber-200/80 list-disc pl-4">
                              {results.validation.errors.slice(0, 4).map((finding: string, index: number) => (
                                <li key={index}>{finding}</li>
                              ))}
                              {results.validation.errors.length > 4 && (
                                <li>{results.validation.errors.length - 4} additional findings will be evaluated during pre-clear.</li>
                              )}
                            </ul>
                          )}
                          {Array.isArray(results.validation.warnings) && results.validation.warnings.length > 0 && (
                            <>
                              <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                Warnings (trades may proceed)
                              </div>
                              <ul className="mt-1 space-y-1 text-xs text-slate-300/80 list-disc pl-4">
                                {results.validation.warnings.slice(0, 6).map((warning: string, index: number) => (
                                  <li key={index}>{warning}</li>
                                ))}
                                {results.validation.warnings.length > 6 && (
                                  <li>{results.validation.warnings.length - 6} additional warnings.</li>
                                )}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>
                    </GlassCard>
                  )}

                  {results.execution && (
                    <GlassCard className="p-5 border-blue-500/25 bg-blue-500/5">
                      <div className="flex items-start gap-3">
                        <Activity className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                        <div>
                          <h3 className="text-sm font-bold text-blue-300">Auto-execute result</h3>
                          {results.execution.skipped ? (
                            <p className="text-xs text-slate-400 mt-1">{results.execution.reason || 'Draft creation skipped.'}</p>
                          ) : (
                            <p className="text-xs text-slate-400 mt-1">
                              {results.execution.success_count ?? 0} of {results.execution.total ?? 0} DRAFT trades created
                              {Number(results.execution.reused_count) > 0 && <> ({results.execution.reused_count} reused — idempotent replay)</>}
                              {Array.isArray(results.execution.failed) && results.execution.failed.length > 0 && (
                                <span className="text-red-400"> · {results.execution.failed.length} failed</span>
                              )}
                              {' '}— review them in the blotter before pre-clear.
                            </p>
                          )}
                        </div>
                      </div>
                    </GlassCard>
                  )}

                  {/* Sleeve Allocation Summary Cards — driven by the portfolio's actual sleeves (results.drift_details),
                      not a fixed 8-bucket strip. Falls back to SLEEVE_BUCKETS only if drift_details is empty. */}
                  <SleeveCards>
                    {(() => {
                      const driftEntries = Object.entries(results.drift_details || {});
                      if (driftEntries.length > 0) {
                        // Sort so larger sleeves render first (by |current|), stable for demo readability
                        driftEntries.sort(([, a]: [string, any], [, b]: [string, any]) =>
                          (b?.current ?? 0) - (a?.current ?? 0)
                        );
                        return driftEntries.map(([assetClass, data]: [string, any]) => {
                          const current = Number(data?.current ?? 0);
                          const target = Number(data?.target ?? 0);
                          const drift = Number(data?.difference ?? (current - target));
                          const hasDrift = Math.abs(drift) > 2;
                          return (
                            <SleeveCard key={assetClass} $drift={hasDrift}>
                              <SleeveLabel>{formatAssetClassLabel(assetClass)}</SleeveLabel>
                              <SleeveValue $color={assetClassColor(assetClass)}>
                                {current > 0 ? `${current.toFixed(1)}%` : '--'}
                              </SleeveValue>
                              <SleeveDrift $negative={drift < 0}>
                                Target {target.toFixed(1)}% · {drift > 0 ? '+' : ''}{drift.toFixed(1)}%
                              </SleeveDrift>
                            </SleeveCard>
                          );
                        });
                      }
                      // Fallback for portfolios without a drift detail payload — keep the legacy strip
                      return SLEEVE_BUCKETS.map(bucket => {
                        const alloc = sleeveAllocations[bucket.key];
                        const drift = alloc.targetPct > 0 ? alloc.pct - alloc.targetPct : 0;
                        const hasDrift = Math.abs(drift) > 2;
                        return (
                          <SleeveCard key={bucket.key} $drift={hasDrift}>
                            <SleeveLabel>{bucket.label}</SleeveLabel>
                            <SleeveValue $color={bucket.color}>
                              {alloc.pct > 0 ? `${alloc.pct.toFixed(1)}%` : '--'}
                            </SleeveValue>
                            {alloc.targetPct > 0 && (
                              <SleeveDrift $negative={drift < 0}>
                                {drift > 0 ? '+' : ''}{drift.toFixed(1)}% vs target
                              </SleeveDrift>
                            )}
                          </SleeveCard>
                        );
                      });
                    })()}
                  </SleeveCards>

                  {/* Unfunded Commitments Card */}
                  <CommitmentCard>
                    <div className="flex items-center gap-3 mb-5">
                      <DollarSign className="w-5 h-5 text-violet-400" />
                      <h3 className="text-lg font-bold text-white">Unfunded Commitments</h3>
                    </div>
                    {commitmentSummary ? (() => {
                      // Treat null and 0 the same — if a portfolio holds only fully-funded
                      // instruments (e.g. CLO tranches, public FI) the engine returns zeros.
                      // Showing four $0.0M / -- tiles would look broken; explain it instead.
                      const noUnfunded =
                        (commitmentSummary.totalUnfunded == null || Number(commitmentSummary.totalUnfunded) === 0) &&
                        (commitmentSummary.projected6mo == null || Number(commitmentSummary.projected6mo) === 0) &&
                        (commitmentSummary.projected12mo == null || Number(commitmentSummary.projected12mo) === 0);
                      if (noUnfunded) {
                        return (
                          <div className="py-4 text-sm text-slate-400 leading-relaxed">
                            <span className="text-slate-200 font-semibold">No unfunded commitments.</span>
                            {' '}This portfolio holds only fully-funded instruments — no delayed-draw term loans,
                            revolvers, or capital-call obligations to project. Cash reserve recommendations apply
                            only to portfolios with outstanding undrawn commitments.
                          </div>
                        );
                      }
                      return (
                        <CommitmentGrid>
                          <CommitmentItem>
                            <CommitmentLabel>Total Unfunded</CommitmentLabel>
                            <CommitmentValue>
                              {commitmentSummary.totalUnfunded != null
                                ? `$${(commitmentSummary.totalUnfunded / 1_000_000).toFixed(1)}M`
                                : '--'}
                            </CommitmentValue>
                          </CommitmentItem>
                          <CommitmentItem>
                            <CommitmentLabel>Projected Calls (6mo)</CommitmentLabel>
                            <CommitmentValue>
                              {commitmentSummary.projected6mo != null
                                ? `$${(commitmentSummary.projected6mo / 1_000_000).toFixed(1)}M`
                                : '--'}
                            </CommitmentValue>
                          </CommitmentItem>
                          <CommitmentItem>
                            <CommitmentLabel>Projected Calls (12mo)</CommitmentLabel>
                            <CommitmentValue>
                              {commitmentSummary.projected12mo != null
                                ? `$${(commitmentSummary.projected12mo / 1_000_000).toFixed(1)}M`
                                : '--'}
                            </CommitmentValue>
                          </CommitmentItem>
                          <CommitmentItem>
                            <CommitmentLabel>Capital Called</CommitmentLabel>
                            <CommitmentValue>
                              {commitmentSummary.calledPercentage != null
                                ? `${Number(commitmentSummary.calledPercentage).toFixed(1)}%`
                                : '--'}
                            </CommitmentValue>
                          </CommitmentItem>
                        </CommitmentGrid>
                      );
                    })() : (
                      <div className="py-6 text-center text-slate-500 italic text-sm">
                        Run analysis to see commitment data
                      </div>
                    )}
                  </CommitmentCard>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <GlassCard className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <Layers className="w-5 h-5 text-blue-400" />
                        <h3 className="text-lg font-bold text-white">Allocation Drift</h3>
                      </div>
                      <div className="space-y-4">
                        {Object.entries(results.drift_details || {}).map(([assetClass, data]: [string, any]) => {
                          const current = Number(data?.current ?? 0);
                          const target = Number(data?.target ?? 0);
                          const diff = Number(data?.difference ?? (current - target));
                          // Markers are anchored on the *larger* of current vs target so both fit in the bar.
                          const denom = Math.max(current, target, 1);
                          return (
                            <div key={assetClass} className="space-y-2">
                              <div className="flex justify-between items-end">
                                <span className="text-sm font-bold text-slate-300">{formatAssetClassLabel(assetClass)}</span>
                                <div className="text-right">
                                  <span className={cn(
                                    "text-sm font-bold",
                                    Math.abs(diff) > 5 ? "text-red-400" : "text-emerald-400"
                                  )}>
                                    {diff > 0 ? '+' : ''}{diff.toFixed(2)}%
                                  </span>
                                  <div className="text-[10px] text-slate-400 font-medium">
                                    <span className="text-slate-200">Current {current.toFixed(2)}%</span>
                                    <span className="text-slate-500"> → Target {target.toFixed(1)}%</span>
                                  </div>
                                </div>
                              </div>
                              {/* Bar with both Current (filled) and Target (marker) so the drift is visible at a glance */}
                              <div className="relative h-2 bg-slate-800 rounded-full overflow-hidden">
                                <div
                                  className={cn(
                                    "h-full transition-all duration-1000",
                                    diff > 0 ? "bg-amber-500" : "bg-blue-500"
                                  )}
                                  style={{ width: `${(current / denom) * 100}%` }}
                                />
                                <div
                                  className="absolute top-[-2px] h-3 w-[2px] bg-slate-200/80"
                                  style={{ left: `calc(${(target / denom) * 100}% - 1px)` }}
                                  title={`Target ${target.toFixed(1)}%`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </GlassCard>

                    <GlassCard className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <Zap className="w-5 h-5 text-amber-400" />
                        <h3 className="text-lg font-bold text-white">Recommended Trades</h3>
                      </div>
                      <div className="space-y-3">
                        {results.trades && results.trades.length > 0 ? (
                          results.trades.map((trade: any, idx: number) => {
                            const liquidityType = getLiquidityType(trade);
                            const tradeTier = getTradeClassification(trade);
                            const status = stageStatus[idx];
                            return (
                              <div key={idx} className="flex items-center justify-between p-4 bg-slate-950/30 border border-white/5 rounded-2xl group hover:bg-white/5 transition-all">
                                <div className="flex items-center gap-4">
                                  <div className={cn(
                                    "w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs shadow-lg",
                                    trade.transactionType === 'BUY' ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
                                  )}>
                                    {trade.transactionType}
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-white flex items-center">
                                      ${Number(trade.notionalAmount ?? trade.amount).toLocaleString()}
                                      {trade.securityName && <span className="text-slate-400 ml-2 font-normal text-xs">{trade.securityName}</span>}
                                      <LiquidityBadge $type={liquidityType}>{getLiquidityLabel(liquidityType)}</LiquidityBadge>
                                    </div>
                                    <div className="text-[11px] text-slate-400 font-medium">
                                      {Number(trade.quantity ?? trade.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} par
                                      {trade.price != null && <> @ {Number(trade.price).toFixed(2)}</>}
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{trade.reason}</div>
                                    {status?.state === 'staged' && (
                                      <div className="text-[11px] text-emerald-400 font-semibold mt-1 flex items-center gap-1.5">
                                        <Activity className="w-3 h-3" />
                                        Staged · Pre-clearing
                                        {status.tradeId != null && (
                                          <a
                                            href={`/bond-transactions?query=${status.tradeId}`}
                                            className="ml-1 text-emerald-300 underline underline-offset-2 hover:text-emerald-200"
                                          >
                                            #{status.tradeId}
                                          </a>
                                        )}
                                      </div>
                                    )}
                                    {status?.state === 'failed' && (
                                      <div className="text-[11px] text-red-400 mt-1">
                                        {status.tradeId != null ? `Draft #${status.tradeId} created · pre-clear failed: ${status.error}` : `Failed: ${status.error}`}
                                      </div>
                                    )}
                                    {status?.state === 'blocked' && (
                                      <div className="text-[11px] text-amber-400 mt-1">
                                        Draft #{status.tradeId} created · held by compliance — request an override from the draft to proceed.
                                        <span className="block text-amber-300/70 mt-0.5">{status.error}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {status?.state === 'staged' ? (
                                    <ActionButton $tier="execute" disabled style={{ opacity: 0.6, cursor: 'default' }}>
                                      Staged ✓
                                    </ActionButton>
                                  ) : status?.state === 'submitting' ? (
                                    <ActionButton $tier="execute" disabled style={{ opacity: 0.7, cursor: 'wait' }}>
                                      Staging…
                                    </ActionButton>
                                  ) : status?.state === 'blocked' ? (
                                    <ActionButton
                                      $tier="notice"
                                      onClick={() => { window.location.href = `/bond-transactions?query=${status.tradeId}`; }}
                                    >
                                      Open Draft #{status.tradeId}
                                    </ActionButton>
                                  ) : (
                                    <>
                                      {tradeTier === 'execute' && (
                                        <ActionButton
                                          $tier="execute"
                                          onClick={() => setConfirmStageIdx(idx)}
                                        >
                                          {status?.state === 'failed' ? 'Retry' : 'Execute Now'}
                                        </ActionButton>
                                      )}
                                      {tradeTier === 'notice' && (
                                        <ActionButton
                                          $tier="notice"
                                          onClick={() => setConfirmStageIdx(idx)}
                                        >
                                          {status?.state === 'failed' ? 'Retry' : 'Submit Notice'}
                                        </ActionButton>
                                      )}
                                      {tradeTier === 'manual' && (
                                        <ActionButton
                                          $tier="manual"
                                          onClick={() => setConfirmStageIdx(idx)}
                                        >
                                          {status?.state === 'failed' ? 'Retry' : 'Stage Manual'}
                                        </ActionButton>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="py-12 text-center text-slate-500 italic text-sm">
                            {results.plan_gaps && results.plan_gaps.length > 0
                              ? 'No executable trades — every drift entry is blocked. See the unfilled gaps below.'
                              : 'No trades required to reach target allocations'}
                          </div>
                        )}
                      </div>
                    </GlassCard>

                    {results.plan_gaps && results.plan_gaps.length > 0 && (
                      <GlassCard className="p-6 border-amber-500/20 bg-amber-500/5">
                        <div className="flex items-center gap-3 mb-4">
                          <AlertCircle className="w-5 h-5 text-amber-400" />
                          <h3 className="text-lg font-bold text-white">Unfilled Plan Gaps</h3>
                          <span className="text-[10px] text-amber-400/80 font-bold uppercase tracking-widest">Partial plan</span>
                        </div>
                        <p className="text-xs text-slate-400 mb-4">
                          These drift entries could not be turned into trades. The trades above are still executable,
                          but the portfolio will not fully reach target until each gap is resolved.
                        </p>
                        <div className="space-y-2">
                          {results.plan_gaps.map((gap: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-950/30 border border-amber-500/10 rounded-xl">
                              <div className="flex items-center gap-3">
                                <div className={cn(
                                  "w-9 h-9 rounded-lg flex items-center justify-center font-bold text-[10px]",
                                  gap.side === 'BUY' ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
                                )}>
                                  {gap.side}
                                </div>
                                <div>
                                  <div className="text-sm font-bold text-white">
                                    {gap.assetClass}
                                    <span className="text-amber-400 ml-2 font-semibold text-xs">
                                      ${Number(gap.unfilledAmount).toLocaleString()} unfilled
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-slate-400">{gap.reason}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </GlassCard>
                    )}
                  </div>
                </>
              ) : (
                <GlassCard className="p-12 text-center border-red-500/20 bg-red-500/5">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Analysis Failed</h3>
                  <p className="text-red-400/80 max-w-md mx-auto">{results.error}</p>
                </GlassCard>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </MainContent>

      {/* Confirm-and-stage modal — fires when user clicks Execute Now on a row.
          On confirm: POST /api/trade-transactions (DRAFT) + POST /api/pre-clear/{id}/submit.
          Card flips to "Staged · Pre-clearing" with a link to the trade in the blotter. */}
      <AnimatePresence>
        {confirmStageIdx != null && results?.trades?.[confirmStageIdx] && (() => {
          const trade = results.trades[confirmStageIdx];
          const portfolioName = portfolios.find(p => p.id.toString() === selectedPortfolioId)?.portfolioName || 'portfolio';
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
              onClick={() => setConfirmStageIdx(null)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 10 }}
                transition={{ duration: 0.18 }}
                className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
                  <div className={cn(
                    "w-9 h-9 rounded-xl flex items-center justify-center",
                    trade.transactionType === 'BUY' ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
                  )}>
                    <Zap className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">Stage trade for pre-clear?</h3>
                    <p className="text-[11px] text-slate-400">{portfolioName}</p>
                  </div>
                </div>
                <div className="px-6 py-5 space-y-4">
                  <div className="bg-slate-950/40 border border-white/5 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Side</span>
                      <span className={cn(
                        "font-bold",
                        trade.transactionType === 'BUY' ? "text-emerald-400" : "text-red-400"
                      )}>{trade.transactionType}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Notional</span>
                      <span className="font-bold text-white">${Number(trade.notionalAmount ?? trade.amount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Par / Price</span>
                      <span className="font-bold text-white">
                        {Number(trade.quantity ?? trade.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        {trade.price != null && <span className="text-slate-300 font-medium"> @ {Number(trade.price).toFixed(2)}</span>}
                      </span>
                    </div>
                    {trade.securityName && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Security</span>
                        <span className="text-white text-right">{trade.securityName}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs pt-2 border-t border-white/5">
                      <span className="text-slate-500">Reason</span>
                      <span className="text-slate-300 text-right max-w-[260px]">{trade.reason}</span>
                    </div>
                  </div>
                  <p className="text-[12px] text-slate-400 leading-relaxed">
                    This creates a <span className="text-white font-semibold">DRAFT</span> trade in IBOR and submits it for{' '}
                    <span className="text-white font-semibold">pre-clear</span>. Compliance rules run automatically; the PM reviews the result in the blotter.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-3 bg-slate-950/40">
                  <button
                    onClick={() => setConfirmStageIdx(null)}
                    className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-white/5 transition-all uppercase tracking-widest"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStageTrade(trade, confirmStageIdx)}
                    className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest"
                  >
                    Stage & Pre-clear
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Manage Targets Modal — CRUD over /api/target-allocations */}
      <AnimatePresence>
        {showTargetAllocationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
            onClick={() => !targetSaving && setShowTargetAllocationModal(false)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.18 }}
              className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                    <Target className="w-4 h-4 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">Manage Target Allocations</h3>
                    <p className="text-[11px] text-slate-400">
                      {portfolios.find(p => p.id.toString() === selectedPortfolioId)?.portfolioName || 'Portfolio'}
                      {targetForm.existingId ? ' · Editing active strategy' : ' · New strategy'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => !targetSaving && setShowTargetAllocationModal(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-white/5 hover:text-white"
                  disabled={targetSaving}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                    Strategy Name
                  </label>
                  <input
                    type="text"
                    value={targetForm.strategyName}
                    onChange={(e) => setTargetForm(prev => ({ ...prev, strategyName: e.target.value }))}
                    placeholder="e.g. CLO Opportunistic — Equal Weight"
                    className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500"
                    disabled={targetSaving}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                      Sleeves &amp; Target %
                    </label>
                    <span
                      className={cn(
                        "text-[11px] font-bold",
                        Math.abs(targetFormTotal - 100) <= 1 ? "text-emerald-400" : "text-amber-400"
                      )}
                    >
                      Sum: {targetFormTotal.toFixed(2)}% / 100%
                    </span>
                  </div>
                  <div className="space-y-2">
                    {targetForm.rows.map((row, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          list="rebalancer-target-asset-classes"
                          type="text"
                          value={row.assetClass}
                          onChange={(e) => handleTargetRowChange(idx, 'assetClass', e.target.value)}
                          placeholder="Asset class (e.g. CLO_EQUITY)"
                          className="flex-1 bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500"
                          disabled={targetSaving}
                        />
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={row.pct}
                            onChange={(e) => handleTargetRowChange(idx, 'pct', e.target.value)}
                            className="w-24 bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white text-right pr-7 focus:outline-none focus:border-indigo-500"
                            disabled={targetSaving}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">%</span>
                        </div>
                        <button
                          onClick={() => handleRemoveTargetRow(idx)}
                          disabled={targetSaving}
                          className="p-2 rounded-lg text-slate-500 hover:bg-red-500/10 hover:text-red-400"
                          aria-label="Remove sleeve"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <datalist id="rebalancer-target-asset-classes">
                      {assetClassOptions.map(opt => <option key={opt} value={opt} />)}
                    </datalist>
                  </div>
                  <button
                    onClick={handleAddTargetRow}
                    disabled={targetSaving}
                    className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-indigo-300 hover:text-indigo-200"
                  >
                    <PlusCircle className="w-4 h-4" /> Add Sleeve
                  </button>
                </div>

                {targetSaveError && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{targetSaveError}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/10 bg-slate-950/40">
                <button
                  onClick={() => setShowTargetAllocationModal(false)}
                  disabled={targetSaving}
                  className="px-4 py-2 text-xs font-bold text-slate-300 hover:bg-white/5 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTargets}
                  disabled={targetSaving || Math.abs(targetFormTotal - 100) > 1.0 || targetForm.rows.length === 0}
                  className="px-4 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg flex items-center gap-2 shadow-lg shadow-indigo-600/20"
                >
                  {targetSaving ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5" />}
                  {targetForm.existingId ? 'Update Targets' : 'Save Targets'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default PortfolioRebalancingScreen;
